import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  combineLatest,
  forkJoin,
  interval,
  of,
  Subscription,
} from "rxjs";
import { PhotoScanStep } from "../constants";
import { catchError, map, switchMap, take, tap } from "rxjs/operators";
import * as Sentry from "@sentry/angular-ivy";
import { HaleonDataAccessService } from "./haleon-data-access.service";
import {
  Image,
  ImageService,
  Patient,
  Treatment,
  TreatmentService,
} from "@kells/clinic-one/apis";
import { ReviewJob } from "@kells/clinic-one/data-access/review-jobs";
import { isEmpty } from "lodash";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  keepDefined,
  retryWithDelay,
} from "@kells/utils/observable/observable-operators";
import {
  CariesStageTypes,
  FindingsByImage,
  FindingType,
  RenderableFindingBase,
} from "@kells/interfaces/finding";
import { HaleonAuthService } from "./haleon-auth.service";
import { mergeMap } from "rxjs/operators";

const MAX_RETRIES = 1;
const RETRY_DELAY = 1000;

@Injectable({
  providedIn: "root",
})
export class HaleonSharedService {
  public readonly reportLoadingSubject = new BehaviorSubject<boolean>(false);
  public readonly reportLoading$ = this.reportLoadingSubject.asObservable();

  public readonly sessionSubject = new BehaviorSubject<ReviewJob | null>(null);
  public readonly session$ = this.sessionSubject.asObservable();

  public readonly imagesSubject = new BehaviorSubject<Image[] | null>(null);
  public readonly images$ = this.imagesSubject.asObservable();

  public readonly treatmentsSubject = new BehaviorSubject<Treatment[] | null>(
    null
  );
  public readonly treatments$ = this.treatmentsSubject.asObservable();

  public readonly hasReportSubject = new BehaviorSubject<boolean>(false);
  public readonly hasReport$ = this.hasReportSubject.asObservable();

  public readonly patientSubject = new BehaviorSubject<Patient | null>(null);
  public readonly patient$ = this.patientSubject.asObservable();

  public readonly isSubmittingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  public readonly isSubmitting$ = this.isSubmittingSubject.asObservable();

  private refreshSubscription?: Subscription;
  private readonly REPORT_REFRESH_INTERVAL = 10000; // 10 seconds

  public readonly sessionScore$ = this.treatments$.pipe(
    keepDefined(),
    map((treatments) => {
      return this.treatmentService.computeTreatmentSessionScore(treatments);
    })
  );

  public readonly findings$ = this.images$.pipe(
    keepDefined(),
    map((images) => images.map((image) => image.findings)),
    map((findings) => findings.flat())
  );

  public readonly hasFindings$ = this.findings$.pipe(
    map((findings) => {
      return findings.length > 0;
    })
  );

  // Build per-image finding aggregations similar to `DataAccessService.createFindingsByImage`.
  public readonly findingsByImages$ = combineLatest([
    this.images$.pipe(keepDefined()),
    this.session$.pipe(keepDefined()),
  ]).pipe(
    map(([images, session]) => {
      const sessionName = session?.sessionDate ?? "";
      const sessionId = session?.id ?? "";

      return images.map((image, index) => {
        return this.createFindingsByImage(image, sessionName, sessionId, index);
      });
    })
  );

  private createFindingsByImage(
    image: Image,
    session: string,
    sessionId: string,
    index: number
  ): FindingsByImage {
    const obj: FindingsByImage = {
      name: `${session}_${
        image?.templateId != null ? image.templateId : index + 1
      }`,
      session,
      sessionId,
      image,
      image_id: image.id,
      image_url: image.url,
      imageAppearances: 1,
      allFindings: image.findings as RenderableFindingBase[],
      totalCaries: 0,
      qtyAdvanced: 0,
      qtyInitial: 0,
      qtyModerate: 0,
      qtyMaterial: 0,
      qtyBoneLoss: 0,

      images: [image],
      xrays: [],
      allCaries: [],
      treatments: [],
      tooth: [],
      initial: [],
      advanced: [],
      moderate: [],
      material: [],
      boneLoss: [],
      calculus: [],
      fracture: [],
      infection: [],
      defectiveRestoration: [],
      plaque: [],
      gumRecession: [],
      gumInflammation: [],
      missingTooth: [],
      imageType: image.imageType,
    };

    image.findings.forEach((fnd) => {
      switch (fnd.type) {
        case FindingType.Tooth:
          obj.tooth.push(fnd as RenderableFindingBase);
          break;
        case FindingType.Material:
          obj.material.push(fnd as RenderableFindingBase);
          break;
        case FindingType.BoneLoss:
          obj.boneLoss.push(fnd as RenderableFindingBase);
          break;
        case FindingType.Fracture:
          obj.fracture.push(fnd as RenderableFindingBase);
          break;
        case FindingType.Calculus:
          obj.calculus.push(fnd as RenderableFindingBase);
          break;
        case FindingType.Infection:
          obj.infection.push(fnd as RenderableFindingBase);
          break;
        case FindingType.DefectiveRestoration:
          obj.defectiveRestoration.push(fnd as RenderableFindingBase);
          break;
        case FindingType.Plaque:
          obj.plaque.push(fnd as RenderableFindingBase);
          break;
        case FindingType.GumRecession:
          obj.gumRecession.push(fnd as RenderableFindingBase);
          break;
        case FindingType.GumInflammation:
          obj.gumInflammation.push(fnd as RenderableFindingBase);
          break;
        case FindingType.MissingTooth:
          obj.missingTooth.push(fnd as RenderableFindingBase);
          break;
        case FindingType.Caries:
          obj.allCaries.push(fnd as RenderableFindingBase);
          if (fnd.stage === CariesStageTypes.Initial) {
            obj.initial.push(fnd as RenderableFindingBase);
          } else if (fnd.stage === CariesStageTypes.Moderate) {
            obj.moderate.push(fnd as RenderableFindingBase);
          } else if (fnd.stage === CariesStageTypes.Advanced) {
            obj.advanced.push(fnd as RenderableFindingBase);
          }
      }
    });

    return obj;
  }

  private createdSessionId: string | null = null;

  public readonly currentStepSubject = new BehaviorSubject<PhotoScanStep>(
    PhotoScanStep.GET_STARTED
  );

  public readonly currentStep$ = this.currentStepSubject.asObservable();

  public readonly stepProgress$ = this.currentStep$.pipe(
    map((step) => {
      const stepsCount = Object.values(PhotoScanStep).length;

      return (
        ((Object.values(PhotoScanStep).indexOf(step) + 1) * 100) / stepsCount
      );
    })
  );

  constructor(
    private readonly haleonDataAccessService: HaleonDataAccessService,
    private readonly snackBar: MatSnackBar,
    private readonly treatmentService: TreatmentService,
    private readonly haleonAuthService: HaleonAuthService
  ) {}

  public setCurrentStep(step: PhotoScanStep): void {
    this.currentStepSubject.next(step);
  }

  public getPatientReport(email: string) {
    this.reportLoadingSubject.next(true);

    return this.haleonDataAccessService.getPatientReport(email).pipe(
      tap(({ patient, session, images, treatments }) => {
        this.reportLoadingSubject.next(false);
        this.patientSubject.next(patient);
        this.sessionSubject.next(session);

        const treatmentsForSession = treatments.filter(
          (t) => t.session === session?.id
        );
        this.treatmentsSubject.next(treatmentsForSession);

        const sessionImages = images[session?.sessionDate ?? ""] ?? [];
        const parsedImages: Image[] =
          sessionImages?.map((img) => {
            const image = ImageService.parseImageResponse(img);
            return {
              ...image,
              findings: image.findings.filter(
                (f) =>
                  f.type === FindingType.Caries ||
                  f.type === FindingType.Fracture ||
                  f.type === FindingType.Plaque
              ),
            };
          }) ?? [];
        this.imagesSubject.next(parsedImages);

        const hasReport = !isEmpty(session) && !isEmpty(images);
        this.hasReportSubject.next(hasReport);
        if (hasReport) {
          const sessionCompleted = session?.completed;
          this.setCurrentStep(
            sessionCompleted
              ? PhotoScanStep.REPORT_GENERATED
              : PhotoScanStep.REPORT_GENERATING
          );
        }
      })
    );
  }

  /**
   * After session is marked ready: run getPhotoPredictions for each uploaded image,
   */
  private runPhotoPredictionsThenComplete(imageIds: string[]) {
    if (imageIds.length === 0) {
      return of(undefined);
    }

    return forkJoin(
      imageIds.map((imageId) =>
        this.haleonDataAccessService.getPhotoPredictions(imageId).pipe(
          catchError((e) => {
            Sentry.captureException(e);
            Sentry.captureMessage(
              `[Haleon] getPhotoPredictions failed for image ${imageId}: ${e.message}`
            );
            return of([]);
          })
        )
      )
    ).pipe(map(() => undefined));
  }

  public setupBackgroundRefreshIfNeeded(email: string): void {
    // Clean up existing subscription if any
    this.stopBackgroundRefresh();

    // Check current state
    const session = this.sessionSubject.value;

    if (session && !session.completed) {
      // Set up background refresh
      this.refreshSubscription = interval(this.REPORT_REFRESH_INTERVAL)
        .pipe(
          mergeMap(() => this.getPatientReport(email)),
          tap(() => {
            // After each refresh, check if session is now completed
            const currentSession = this.sessionSubject.value;
            if (currentSession?.completed) {
              // Session completed, stop refresh
              this.stopBackgroundRefresh();
            }
          })
        )
        .subscribe();
    }
  }

  /**
   * Stops the background refresh subscription
   */
  public stopBackgroundRefresh(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
      this.refreshSubscription = undefined;
    }
  }

  public submitSession(images: File[]) {
    this.isSubmittingSubject.next(true);

    const patientSession$ = this.haleonAuthService.getAuth().pipe(
      take(1),
      catchError((e) => {
        Sentry.captureException(e);
        Sentry.captureMessage(`[Haleon] Auth retrieval failed: ${e.message}`);
        throw new Error(e.message);
      }),
      switchMap((auth) => {
        // Get patient using email from auth
        return this.haleonDataAccessService.getPatient(auth.email).pipe(
          switchMap((patient) => {
            return this.createdSessionId
              ? of({ session_id: this.createdSessionId, patient })
              : this.haleonDataAccessService.createSession(patient).pipe(
                  take(1),
                  map((session) => ({
                    session_id: session.session_id,
                    patient,
                  })),
                  catchError((e) => {
                    Sentry.captureException(e);
                    Sentry.captureMessage(
                      `[Haleon] Session creation failed: ${e.message}`
                    );
                    this.isSubmittingSubject.next(false);
                    // If session creation fails (e.g. no free scans left), clear the auth so the user can start fresh
                    this.haleonAuthService.resetAuth();
                    this.createdSessionId = null;
                    this.snackBar.open("Failed to submit checkup.", "", {
                      duration: 2000,
                      panelClass: "ai-photo-scan-submit-error-bar",
                      verticalPosition: "top",
                    });
                    throw new Error(e.message);
                  })
                );
          }),
          catchError((e) => {
            Sentry.captureException(e);
            Sentry.captureMessage(
              `[Haleon] Patient retrieval failed: ${e.message}`
            );
            this.haleonAuthService.resetAuth();
            throw new Error(e.message);
          })
        );
      })
    );

    return patientSession$.pipe(take(1)).subscribe({
      next: ({ session_id: sessionId, patient }) => {
        this.createdSessionId = sessionId;
        Sentry.captureMessage(`[Haleon] Session created: ${sessionId}`);
        Sentry.captureMessage(
          `[Haleon] Uploading images for session: ${sessionId}`
        );

        const uploadObservables = images.map((file, idx) => {
          Sentry.captureMessage(
            `[Haleon] Uploading image #${
              idx + 1
            } for session: ${sessionId}. File name: ${file.name}.`
          );

          return this.haleonDataAccessService
            .uploadImageForPatient(patient, file, sessionId)
            .pipe(
              tap(() =>
                Sentry.captureMessage(
                  `[Haleon] Successfully uploaded image #${
                    idx + 1
                  } for session: ${sessionId}. File name: ${file.name}.`
                )
              ),
              retryWithDelay(
                MAX_RETRIES,
                RETRY_DELAY,
                (error, attempt, retries) => {
                  Sentry.captureMessage(
                    `[Haleon] Retrying image upload #${
                      idx + 1
                    } for session ${sessionId} (retry ${
                      attempt + 1
                    } of ${retries}) due to error: ${
                      error.message
                    }. File name: ${file.name}.`
                  );
                }
              ),
              catchError((e) => {
                Sentry.captureException(e);
                Sentry.captureMessage(
                  `[Haleon] Image upload failed for image #${
                    idx + 1
                  } in session ${sessionId}: ${e.message}. File name: ${
                    file.name
                  }.`
                );
                return of(null);
              })
            );
        });

        forkJoin(uploadObservables).subscribe((results) => {
          const validImagesIds = results.filter((r): r is string => !!r);
          const successCount = validImagesIds.length;
          Sentry.captureMessage(
            `[Haleon] Image upload results for session ${sessionId}: ${successCount} succeeded, ${
              images.length - successCount
            } failed`
          );
          if (successCount >= 3) {
            Sentry.captureMessage(
              `[Haleon] Marking session as ready: ${sessionId}`
            );
            this.haleonDataAccessService
              .markSessionReady(sessionId)
              .pipe(take(1))
              .subscribe({
                next: () => {
                  Sentry.captureMessage(
                    `[Haleon] Session marked as ready: ${sessionId}`
                  );
                  const patientEmail = patient?.email;
                  if (!patientEmail) {
                    Sentry.captureMessage(
                      `[Haleon] Patient email is missing, skipping report refresh`
                    );
                    this.isSubmittingSubject.next(false);
                    return;
                  }

                  this.getPatientReport(patientEmail)
                    .pipe(
                      tap(() =>
                        this.setupBackgroundRefreshIfNeeded(patientEmail)
                      )
                    )
                    .subscribe({
                      next: () => this.isSubmittingSubject.next(false),
                      error: () => this.isSubmittingSubject.next(false),
                    });
                  this.runPhotoPredictionsThenComplete(validImagesIds)
                    .pipe(switchMap(() => this.getPatientReport(patientEmail)))
                    .subscribe({
                      next: () => this.isSubmittingSubject.next(false),
                      error: () => this.isSubmittingSubject.next(false),
                    });
                },
                error: () => {
                  this.isSubmittingSubject.next(false);
                },
              });
          } else {
            Sentry.captureMessage(
              `[Haleon] Not enough images uploaded for session ${sessionId}. Required: 3, Uploaded: ${successCount}`
            );
            this.isSubmittingSubject.next(false);
            this.snackBar.open(
              "Failed to submit scans. Please try again.",
              "",
              {
                duration: 2000,
                panelClass: "ai-photo-scan-submit-error-bar",
                verticalPosition: "top",
              }
            );
          }
        });
      },
      error: (e) => {
        Sentry.captureException(e);
        Sentry.captureMessage(
          `[Haleon] Session creation process failed: ${e.message}`
        );
        this.isSubmittingSubject.next(false);
      },
    });
  }

  public resetSession(): void {
    this.stopBackgroundRefresh();
    this.createdSessionId = null;
    this.sessionSubject.next(null);
    this.imagesSubject.next(null);
    this.treatmentsSubject.next(null);
    this.hasReportSubject.next(false);
    this.patientSubject.next(null);
    this.isSubmittingSubject.next(false);
    this.haleonAuthService.resetAuth();
    this.currentStepSubject.next(PhotoScanStep.GET_STARTED);
  }
}
